import { useEffect, useState, useCallback, useMemo } from "react";
import { Check, ChevronsUpDown, X, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { normalizeSearch } from "@/lib/normalize-search";
import { fetchAllPages } from "@/lib/supabase-pagination";
import { isAgentUsingNetfaceIntegration } from "@/lib/netface-integration-agents";

interface NetfaceIntegrationState {
  assigned_agent_id: string | null;
  config: Record<string, unknown> | null;
}

type ProductType = "product" | "service" | "plan";
type TypeFilter = "all" | ProductType;

const TYPE_FILTER_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "product", label: "Produtos" },
  { value: "service", label: "Serviços" },
  { value: "plan", label: "Planos" },
];

const TYPE_LABEL: Record<ProductType, string> = {
  product: "Produto",
  service: "Serviço",
  plan: "Plano",
};

interface Product {
  id: string;
  name: string;
  price: number | null;
  type: ProductType;
  source: string | null;
}

interface AgentProductSelectorProps {
  workspaceId: string;
  agentId?: string | null;
  selectedProductIds: string[];
  onSelectionChange: (productIds: string[]) => void;
}

const RENDER_CAP = 200;

export function AgentProductSelector({
  workspaceId,
  agentId,
  selectedProductIds,
  onSelectionChange,
}: AgentProductSelectorProps) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [netfaceIntegration, setNetfaceIntegration] = useState<NetfaceIntegrationState | null>(null);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const all = await fetchAllPages<Product>((from, to) =>
        supabase
          .from("products")
          .select("id, name, price, type, source")
          .eq("workspace_id", workspaceId)
          .eq("is_active", true)
          .order("name")
          .range(from, to),
      );
      setProducts(all);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  const fetchNetfaceIntegration = useCallback(async () => {
    if (!workspaceId) return;
    const { data } = await supabase
      .from("integrations")
      .select("assigned_agent_id, config")
      .eq("workspace_id", workspaceId)
      .eq("provider", "netface" as never)
      .in("status", ["active", "pending"])
      .maybeSingle();
    setNetfaceIntegration(
      data
        ? {
          assigned_agent_id: data.assigned_agent_id ?? null,
          config: (data.config && typeof data.config === "object" && !Array.isArray(data.config))
            ? data.config as Record<string, unknown>
            : null,
        }
        : null,
    );
  }, [workspaceId]);

  useEffect(() => {
    fetchProducts();
    void fetchNetfaceIntegration();
  }, [fetchProducts, fetchNetfaceIntegration]);

  const agentUsesNetface = isAgentUsingNetfaceIntegration(agentId, netfaceIntegration);

  const netfaceManagedProductIds = useMemo(
    () => (agentUsesNetface
      ? products.filter((p) => p.source === "netface").map((p) => p.id)
      : []),
    [products, agentUsesNetface],
  );

  const manualProducts = useMemo(
    () => products.filter((p) => p.source !== "netface"),
    [products],
  );

  const effectiveSelectedIds = useMemo(
    () => [...new Set([...selectedProductIds, ...netfaceManagedProductIds])],
    [selectedProductIds, netfaceManagedProductIds],
  );

  useEffect(() => {
    if (netfaceManagedProductIds.length === 0) return;
    const missing = netfaceManagedProductIds.filter((id) => !selectedProductIds.includes(id));
    if (missing.length > 0) {
      onSelectionChange([...new Set([...selectedProductIds, ...missing])]);
    }
  }, [netfaceManagedProductIds, selectedProductIds, onSelectionChange]);

  const toggleProduct = useCallback((productId: string) => {
    if (netfaceManagedProductIds.includes(productId)) return;

    const newSelection = selectedProductIds.includes(productId)
      ? selectedProductIds.filter((id) => id !== productId)
      : [...selectedProductIds, productId];

    onSelectionChange(newSelection);
  }, [selectedProductIds, onSelectionChange, netfaceManagedProductIds]);

  const productsByType = useMemo(() => {
    if (typeFilter === "all") return products;
    return products.filter((p) => p.type === typeFilter);
  }, [products, typeFilter]);

  const allSelected =
    productsByType.length > 0 &&
    productsByType.every((p) => effectiveSelectedIds.includes(p.id));

  const toggleSelectAll = useCallback(() => {
    const filteredIds = new Set(productsByType.map((p) => p.id));
    if (allSelected) {
      onSelectionChange(
        selectedProductIds.filter((id) => !filteredIds.has(id) || netfaceManagedProductIds.includes(id)),
      );
    } else {
      onSelectionChange([...new Set([...selectedProductIds, ...productsByType.map((p) => p.id)])]);
    }
  }, [allSelected, productsByType, selectedProductIds, onSelectionChange, netfaceManagedProductIds]);

  const selectedInViewCount = useMemo(
    () => productsByType.filter((p) => effectiveSelectedIds.includes(p.id)).length,
    [productsByType, effectiveSelectedIds],
  );

  // Filtro client-side accent-insensitive (sobre o tipo já filtrado)
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return productsByType;
    const q = normalizeSearch(searchQuery);
    return productsByType.filter((p) => normalizeSearch(p.name).includes(q));
  }, [productsByType, searchQuery]);

  // Cap de renderização para manter UI responsiva com catálogos grandes
  const visibleProducts = useMemo(
    () => filteredProducts.slice(0, RENDER_CAP),
    [filteredProducts]
  );
  const hiddenCount = filteredProducts.length - visibleProducts.length;

  const selectedProducts = products.filter((p) =>
    effectiveSelectedIds.includes(p.id)
  );

  const unlinkedCount = manualProducts.filter((p) => !effectiveSelectedIds.includes(p.id)).length;
  const showUnlinkedBanner = !isLoading && manualProducts.length > 0 && unlinkedCount > 0;

  return (
    <div className="space-y-3">
      {showUnlinkedBanner && (
        <div className="flex items-start gap-2 rounded-md border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 p-3 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 text-yellow-700 dark:text-yellow-300 shrink-0" />
          <div className="flex-1">
            <p className="text-yellow-900 dark:text-yellow-100">
              <strong>{unlinkedCount}</strong> {unlinkedCount === 1 ? "produto do catálogo não está vinculado" : "produtos do catálogo não estão vinculados"} a este agente. A IA não poderá citá-los nas respostas.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelectionChange([
                ...new Set([...selectedProductIds, ...manualProducts.map((p) => p.id)]),
              ]);
            }}
            className="h-7 text-xs shrink-0"
          >
            Vincular todos
          </Button>
        </div>
      )}
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setSearchQuery("");
            setTypeFilter("all");
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {effectiveSelectedIds.length > 0
              ? `${effectiveSelectedIds.length} produto(s) selecionado(s)`
              : "Selecione produtos..."}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
      </PopoverTrigger>
        <PopoverContent 
          className="w-[400px] p-0 z-50 flex flex-col overflow-hidden max-h-[60vh]" 
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <Command className="flex flex-col overflow-hidden h-auto" shouldFilter={false}>
            <div className="flex flex-wrap gap-1.5 border-b px-3 py-2.5">
              {TYPE_FILTER_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  size="sm"
                  variant={typeFilter === opt.value ? "default" : "outline"}
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setTypeFilter(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            <CommandInput
              placeholder={
                typeFilter === "all"
                  ? "Buscar por nome..."
                  : `Buscar ${TYPE_FILTER_OPTIONS.find((o) => o.value === typeFilter)?.label.toLowerCase()}...`
              }
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <div className="flex items-center justify-between px-3 py-2 border-b">
              <span className="text-sm text-muted-foreground">
                {isLoading
                  ? "Carregando..."
                  : `${selectedInViewCount} de ${productsByType.length} selecionado(s)`}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleSelectAll();
                }}
                disabled={isLoading || productsByType.length === 0}
                className="h-7 text-xs"
              >
                {allSelected ? "Desselecionar Todos" : "Selecionar Todos"}
              </Button>
            </div>
            <CommandList 
              className="flex-1 min-h-0 max-h-[300px] overflow-y-auto touch-pan-y"
              onWheelCapture={(e) => e.stopPropagation()}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Carregando produtos...
                </div>
              ) : (
                <>
                  <CommandEmpty>
                    {typeFilter === "all"
                      ? "Nenhum item encontrado."
                      : `Nenhum ${TYPE_LABEL[typeFilter].toLowerCase()} encontrado.`}
                  </CommandEmpty>
                  <CommandGroup>
                    {visibleProducts.map((product) => {
                      const isNetfaceManaged = product.source === "netface";
                      return (
                      <CommandItem
                        key={product.id}
                        value={product.id}
                        onSelect={() => toggleProduct(product.id)}
                        className={isNetfaceManaged ? "opacity-80" : undefined}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            effectiveSelectedIds.includes(product.id)
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <p className="font-medium truncate">{product.name}</p>
                            {isNetfaceManaged && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                                Netface
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {typeFilter === "all" && (
                              <span>{TYPE_LABEL[product.type]} · </span>
                            )}
                            R$ {product.price !== null ? product.price.toFixed(2) : "0,00"}
                          </p>
                        </div>
                      </CommandItem>
                    );
                    })}
                  </CommandGroup>
                  {hiddenCount > 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground text-center border-t bg-muted/30">
                      Mostrando {visibleProducts.length} de {filteredProducts.length} — refine sua busca para ver mais
                    </div>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedProducts.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-medium">
              Produtos Selecionados ({effectiveSelectedIds.length})
            </h5>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSelectionChange([...netfaceManagedProductIds])}
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
            >
              Limpar todos
            </Button>
          </div>
          <ScrollArea className="max-h-48 rounded-md border p-3">
            <div className="flex flex-wrap gap-2">
              {selectedProducts.map((product) => {
                const isNetfaceManaged = product.source === "netface";
                return (
                <Badge
                  key={product.id}
                  variant="secondary"
                  className="pr-1 flex items-center gap-1"
                >
                  <span className="max-w-[200px] truncate">{product.name}</span>
                  {!isNetfaceManaged && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleProduct(product.id);
                    }}
                    className="ml-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/80 p-0.5 transition-colors"
                    aria-label={`Remover ${product.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  )}
                </Badge>
              );
              })}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
